/**
 * Spook Supply — cart helpers layered on Horizon's native cart (loaded on every page).
 *
 * Horizon owns every cart mutation and re-render (cart-items-component, theme-drawer,
 * Shopify standard cart actions/events). This module only:
 *   1. Announces the result of a cart change once, from a single live region that sits
 *      inside the open drawer (or the page when there is no drawer): e.g.
 *      "Basket updated. You're £7.01 away from FREE UK delivery".
 *   2. Offers a short "Undo" after a line is removed (remove button or quantity 0). Undo re-adds
 *      the same variant and quantity through Shopify's standard cart action. Lines with
 *      custom properties, selling plans or bundle components are not offered undo.
 *   3. Keeps keyboard focus inside the drawer when a re-render removes the focused control
 *      (e.g. a suggestion that was just added, or a removed line).
 */
import { StandardEvents } from '@shopify/events';

const drawerEl = () => /** @type {HTMLElement | null} */ (document.querySelector('theme-drawer#cart-drawer'));
const dialogEl = () => /** @type {HTMLDialogElement | null} */ (drawerEl()?.querySelector('dialog') ?? null);
const drawerOpen = () => Boolean(dialogEl()?.open);
const toGid = (id) => `gid://shopify/ProductVariant/${id}`;

/* ------------------------------------------------------------------ */
/* Live region                                                         */
/* ------------------------------------------------------------------ */
function liveRegion() {
  const host = drawerOpen() ? dialogEl() : document.body;
  let el = host?.querySelector(':scope > .ss-cart-live');
  if (!el && host) {
    el = document.createElement('p');
    el.className = 'ss-cart-live visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    host.append(el);
  }
  return el;
}

let announceTimer = 0;
function announce(message) {
  const el = liveRegion();
  if (!el || !message) return;
  clearTimeout(announceTimer);
  el.textContent = '';
  announceTimer = window.setTimeout(() => {
    el.textContent = message;
  }, 60);
}

function progressText() {
  const scope = drawerOpen() ? dialogEl() : document;
  const msg = scope?.querySelector('[data-ss-cart-ship-msg]');
  return msg ? msg.textContent.replace(/\s+/g, ' ').trim() : '';
}

/* Wait until the cart DOM stops changing (Horizon morphs the section after the event promise). */
function afterRender(callback) {
  const root = drawerOpen() ? dialogEl() : document.querySelector('cart-items-component') || document.body;
  if (!root) return callback();
  let quiet = 0;
  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    observer.disconnect();
    clearTimeout(quiet);
    callback();
  };
  const observer = new MutationObserver(() => {
    clearTimeout(quiet);
    quiet = window.setTimeout(done, 140);
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true });
  quiet = window.setTimeout(done, 400);
  window.setTimeout(done, 1500);
}

/* ------------------------------------------------------------------ */
/* Focus safety                                                        */
/* ------------------------------------------------------------------ */
function rescueFocus(preferred) {
  if (!drawerOpen()) return;
  const dialog = dialogEl();
  const active = document.activeElement;
  if (active && active !== document.body && dialog?.contains(active) && active.isConnected) return;
  const target =
    (preferred && preferred.isConnected && preferred) ||
    dialog?.querySelector('#cart-drawer-heading') ||
    dialog?.querySelector('.theme-drawer__close-button');
  if (!target) return;
  if (target.id === 'cart-drawer-heading' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

/* ------------------------------------------------------------------ */
/* Undo after removal                                                  */
/* ------------------------------------------------------------------ */
let toastTimer = 0;
let pendingUndoAnnouncement = '';

/**
 * Reads what is being removed from Horizon's own event: CartLinesUpdateEvent carries the
 * line key (lines[0].id) and is dispatched before the row is animated out, so the row is
 * still in the DOM here.
 * @param {Event & { lines?: Array<{ id?: string, quantity?: number }> }} event
 */
function removalFromEvent(event) {
  const key = event.lines?.[0]?.id;
  if (!key || event.lines?.[0]?.quantity !== 0) return null;
  const scope = event.target instanceof Element ? event.target : document;
  const row = scope.querySelector(`tr[data-key="${CSS.escape(key)}"]`);
  if (!row) return null;
  const variantId = key.split(':')[0];
  const quantityInput = /** @type {HTMLInputElement | null} */ (row.querySelector('input[type="number"], input[data-cart-quantity]'));
  const quantity = parseInt(quantityInput?.defaultValue || quantityInput?.value || '1', 10) || 1;
  const title = row.querySelector('.cart-items__title')?.textContent?.trim() || 'Item';
  const complex =
    row.querySelector('.cart-items__properties, .cart-items__bundle') ||
    row.classList.contains('cart-items__nested-line') ||
    document.querySelector(`tr[data-parent-key="${CSS.escape(key)}"]`) ||
    !/^\d+$/.test(variantId);
  return { variantId, quantity, title, undoable: !complex };
}

function hideToast() {
  clearTimeout(toastTimer);
  document.querySelectorAll('.ss-cart-toast').forEach((toast) => {
    const hadFocus = toast.contains(document.activeElement);
    toast.remove();
    if (hadFocus) rescueFocus();
  });
}

function showUndo(removal) {
  hideToast();
  const inDrawer = drawerOpen();
  const host = inDrawer ? dialogEl() : document.body;
  if (!host) return;

  const toast = document.createElement('div');
  toast.className = `ss-cart-toast${inDrawer ? '' : ' ss-cart-toast--page'}`;
  const text = document.createElement('span');
  text.className = 'ss-cart-toast__text';
  text.textContent = `${removal.title} removed.`;
  const undo = document.createElement('button');
  undo.type = 'button';
  undo.className = 'ss-cart-toast__undo';
  undo.textContent = 'Undo';
  undo.setAttribute('aria-label', `Undo removing ${removal.title}`);
  undo.addEventListener('click', async () => {
    undo.disabled = true;
    try {
      pendingUndoAnnouncement = `${removal.title} restored.`;
      const result = await window.Shopify?.actions?.updateCart?.(
        { lines: [{ merchandiseId: toGid(removal.variantId), quantity: removal.quantity }] },
        { event: { context: 'cart' } }
      );
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    } catch (error) {
      pendingUndoAnnouncement = '';
      announce(`Sorry, ${removal.title} couldn't be restored.`);
      console.warn('[Spook Supply] Undo failed', error);
    }
    hideToast();
  });
  toast.append(text, undo);
  host.append(toast);
  if (inDrawer) undo.focus({ preventScroll: true });
  toastTimer = window.setTimeout(hideToast, 8000);
}

/* ------------------------------------------------------------------ */
/* React to every cart line change (after Horizon has re-rendered)      */
/* ------------------------------------------------------------------ */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const action = event.action;
  const removal = action === 'remove' ? removalFromEvent(event) : null;
  const focusWasInDrawer = drawerOpen() && dialogEl()?.contains(document.activeElement);

  const settle = (ok) => {
    afterRender(() => {
      const progress = progressText();
      let lead = 'Basket updated.';
      if (action === 'add') lead = pendingUndoAnnouncement || 'Added to your basket.';
      if (action === 'remove') lead = removal ? `${removal.title} removed.` : 'Item removed.';
      pendingUndoAnnouncement = '';

      if (!ok) {
        announce('Sorry, your basket could not be updated.');
      } else {
        announce([lead, progress].filter(Boolean).join(' '));
        if (removal?.undoable) showUndo(removal);
      }
      if (focusWasInDrawer || drawerOpen()) rescueFocus();
    });
  };

  const promise = event.promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(({ detail } = {}) => settle(!detail?.didError)).catch((error) => {
      if (error?.name !== 'AbortError') settle(false);
    });
  } else {
    settle(true);
  }
});

/* Close the toast with the drawer so it never lingers into the next open. */
document.addEventListener(
  'close',
  (event) => {
    if (event.target === dialogEl()) hideToast();
  },
  true
);
